// Pure helpers for per-column faceted filtering of the inventory tables.
// A column's filter is a Set of selected display-values; an EMPTY set means the
// filter is inactive (show all). Across columns the filters AND; within a column
// the Set membership is an OR. Blank/null/"—" values collapse to a single BLANK facet.

export const BLANK = '(Blank)';

/** Normalize a raw cell value to its facet string. null / '' / '—' → BLANK. */
export function facetValue(raw: unknown): string {
  if (raw == null) return BLANK;
  const s = String(raw).trim();
  return s === '' || s === '—' ? BLANK : s;
}

/** Distinct facet values across rows for one accessor, sorted (numeric-aware,
 *  BLANK always last). */
export function distinctValues<T>(rows: readonly T[], accessor: (r: T) => unknown): string[] {
  const set = new Set<string>();
  for (const r of rows) set.add(facetValue(accessor(r)));
  return [...set].sort((a, b) =>
    a === BLANK ? 1 : b === BLANK ? -1 : a.localeCompare(b, undefined, { numeric: true }),
  );
}

/** A row passes when, for EVERY column with a non-empty selection, the row's facet
 *  value is in that column's Set. Empty/absent selections are inactive. */
export function rowPasses<T>(
  row: T,
  filters: Record<string, Set<string>>,
  accessors: Record<string, (r: T) => unknown>,
): boolean {
  for (const label of Object.keys(filters)) {
    const sel = filters[label];
    if (!sel || sel.size === 0) continue;
    const acc = accessors[label];
    if (!acc) continue;
    if (!sel.has(facetValue(acc(row)))) return false;
  }
  return true;
}

/** True if any column has an active (non-empty) selection. */
export function anyActive(filters: Record<string, Set<string>>): boolean {
  return Object.values(filters).some((s) => s && s.size > 0);
}
